import { useState } from "react";
import { ArrowDown, ArrowUp, Target } from "lucide-react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { formatRelativeTime } from "@/lib/format";
import { useMatrixMarkets } from "@/sdk/hooks/use-alpha";
import type { MatrixMarket } from "@/sdk/hooks/use-alpha";

type SortField = "nnf_score" | "edge" | "confidence" | "direction" | "market_price";
type SortDir = "asc" | "desc";

function edgeTone(edge: number): "success" | "warning" | "danger" {
  if (edge >= 10) return "success";
  if (edge >= 5) return "warning";
  return "danger";
}

function edgeRowClass(edge: number): string {
  if (edge >= 10) return "border-green-500/20 bg-green-500/5";
  if (edge >= 5) return "border-amber-500/20 bg-amber-500/5";
  return "border-white/5";
}

function edgeBadgeClass(edge: number): string {
  if (edge >= 10) return "bg-green-500/20 text-green-300";
  if (edge >= 5) return "bg-amber-500/20 text-amber-300";
  return "bg-red-500/20 text-red-300";
}

function directionBadge(dir: string): string {
  return dir === "YES"
    ? "bg-emerald-500/20 text-emerald-300"
    : "bg-red-500/20 text-red-300";
}

function SortHeader({
  label,
  field,
  active,
  dir,
  onSort,
}: {
  label: string;
  field: SortField;
  active: boolean;
  dir: SortDir;
  onSort: (f: SortField) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className="flex items-center gap-1 text-xs font-medium text-white/60 hover:text-white/90 transition"
    >
      {label}
      {active && (dir === "desc" ? (
        <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUp className="h-3 w-3" />
      ))}
    </button>
  );
}

function MarketRow({ market }: { market: MatrixMarket }) {
  return (
    <div
      className={`grid grid-cols-[1fr_5rem_5rem_4rem_5rem_6rem] items-center gap-3 rounded border px-3 py-2 ${edgeRowClass(market.edge)}`}
    >
      {/* Question */}
      <div className="min-w-0">
        <p className="truncate text-xs text-white/80">
          {market.question}
        </p>
        <p className="text-[0.65rem] text-white/35">
          {market.category} · {formatRelativeTime(market.last_scanned)}
        </p>
      </div>

      {/* NNF Score */}
      <div className="text-right">
        <span className="text-sm font-mono font-bold text-white/90">
          {market.nnf_score.toFixed(2)}
        </span>
      </div>

      {/* Edge % */}
      <div className="text-right">
        <span
          className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold ${edgeBadgeClass(market.edge)}`}
        >
          {market.edge.toFixed(1)}%
        </span>
      </div>

      {/* Direction */}
      <div className="text-center">
        <span
          className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold ${directionBadge(market.direction)}`}
        >
          {market.direction}
        </span>
      </div>

      {/* Confidence */}
      <div className="text-right">
        <span className="text-xs font-mono text-white/70">
          {(market.confidence * 100).toFixed(0)}%
        </span>
      </div>

      {/* Triggers */}
      <div className="text-right text-xs text-white/50">
        {market.triggers.pending > 0 || market.triggers.filled > 0 ? (
          <span>
            <span className="text-amber-400">{market.triggers.pending}</span>
            {" / "}
            <span className="text-green-400">{market.triggers.filled}</span>
          </span>
        ) : (
          <span className="text-white/25">--</span>
        )}
      </div>
    </div>
  );
}

export function AlphaSignalsPage() {
  const { data, isLoading, dataUpdatedAt } = useMatrixMarkets();
  const [sortField, setSortField] = useState<SortField>("edge");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const markets = data?.markets ?? [];

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sorted = [...markets].sort((a, b) => {
    const av = a[sortField];
    const bv = b[sortField];
    if (typeof av === "number" && typeof bv === "number") {
      return sortDir === "desc" ? bv - av : av - bv;
    }
    const as = String(av);
    const bs = String(bv);
    return sortDir === "desc" ? bs.localeCompare(as) : as.localeCompare(bs);
  });

  const { highEdge, medEdge, nnfSum } = markets.reduce(
    (stats, market) => {
      stats.nnfSum += market.nnf_score;
      if (market.edge >= 10) {
        stats.highEdge += 1;
      } else if (market.edge >= 5) {
        stats.medEdge += 1;
      }
      return stats;
    },
    { highEdge: 0, medEdge: 0, nnfSum: 0 },
  );
  const avgNnf = markets.length > 0 ? nnfSum / markets.length : 0;

  const isLive = dataUpdatedAt > 0 && Date.now() - dataUpdatedAt < 60_000;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Summary stats */}
      <Panel
        title="Alpha Signals"
        description="NNF matrix market analysis"
        actions={
          <div className="flex items-center gap-2 text-xs text-muted">
            <span
              className={`h-2 w-2 rounded-full ${isLive ? "bg-green-500 animate-pulse" : "bg-muted/50"}`}
            />
            {isLive ? "Live" : "Stale"}
          </div>
        }
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(10rem,1fr))]">
          <StatCard
            label="Markets"
            value={String(data?.total ?? 0)}
            hint="In current matrix"
          />
          <StatCard
            label="High Edge (>10%)"
            value={String(highEdge)}
            tone={highEdge > 0 ? "success" : "default"}
            hint="Strong signals"
          />
          <StatCard
            label="Medium Edge (5-10%)"
            value={String(medEdge)}
            tone={medEdge > 0 ? "warning" : "default"}
            hint="Watch list"
          />
          <StatCard
            label="Avg NNF"
            value={avgNnf.toFixed(2)}
            tone={edgeTone(avgNnf * 100)}
            hint="NeuralNetNinja Factor"
          />
        </div>
      </Panel>

      {/* Market table */}
      <Panel title="Market Matrix">
        {isLoading ? (
          <LoadingState text="Loading matrix markets..." />
        ) : sorted.length === 0 ? (
          <div className="py-8 text-center">
            <Target className="mx-auto h-8 w-8 text-white/20" />
            <p className="mt-2 text-sm text-muted">
              No markets in the matrix yet.
            </p>
            <p className="text-xs text-white/25">
              Run a scanner scan or trigger matrix generation to populate.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
            <div className="min-w-[32rem] space-y-1">
              {/* Header row */}
              <div className="grid grid-cols-[1fr_5rem_5rem_4rem_5rem_6rem] items-center gap-3 px-3 py-1.5">
                <span className="text-xs font-medium text-muted">Market</span>
                <div className="text-right">
                  <SortHeader
                    label="NNF"
                    field="nnf_score"
                    active={sortField === "nnf_score"}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                </div>
                <div className="text-right">
                  <SortHeader
                    label="Edge %"
                    field="edge"
                    active={sortField === "edge"}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                </div>
                <div className="text-center">
                  <SortHeader
                    label="Dir"
                    field="direction"
                    active={sortField === "direction"}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                </div>
                <div className="text-right">
                  <SortHeader
                    label="Conf"
                    field="confidence"
                    active={sortField === "confidence"}
                    dir={sortDir}
                    onSort={handleSort}
                  />
                </div>
                <span className="text-right text-xs font-medium text-muted">
                  Triggers
                </span>
              </div>

              {/* Data rows */}
              {sorted.map((market) => (
                <MarketRow key={market.market_id} market={market} />
              ))}
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
